import { useMemo } from 'react';

import { useQueryAssets } from '@/hooks/useAssets';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useKEVVulnerabilities } from '@/hooks/useKEVVulnerabilities';
import { useMy } from '@/hooks/useMy';
import { useGetRisks } from '@/hooks/useRisks';
import { SeedFilter, Sort, useSeeds } from '@/hooks/useSeeds';
import {
  Filter as TechnologyFilter,
  useGetTechnologies,
} from '@/hooks/useTechnology';
import { getDaysAgo } from '@/sections/insights/metrics/utils/dateUtils';
import { getDefaultSort } from '@/sections/seeds/config/seedTypeConfigs';
import { SeedType } from '@/sections/seeds/types';
import { seedFilterStorage } from '@/sections/seeds/utils/storage/seedFilterStorage';
import { useGlobalState } from '@/state/global.state';
import { STALE_WHILE_REVALIDATE_CACHE } from '@/utils/staleWhileRevalidateCache.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Prefetch data for all primary table pages so they render instantly on navigation.
 *
 * Call this hook on the Insights page so that when the user navigates to any
 * table, the TanStack Query cache is already warm and data renders instantly.
 *
 * Each hook reads persisted filters from localStorage, producing the exact same
 * query keys the real table pages will use — guaranteeing cache hits.
 *
 * These are all async React Query fetches — they do NOT block the main page render.
 */
export function useTablePrefetch() {
  // Assets — reads its own filters from localStorage internally
  useQueryAssets();

  // Vulnerabilities — reads its own filters from localStorage internally
  useGetRisks({ enabled: true, parentFilters: undefined });

  // Technologies — needs filter passed in (matching what the tech page does)
  const [filter] = useStorage<TechnologyFilter>(
    {
      queryKey: 'technologyFilters',
      key: StorageKey.FILTERS_TECHNOLOGIES,
    },
    {
      sort: {
        orderBy: 'name',
        descending: true,
      },
    }
  );
  useGetTechnologies({ filter });

  // Seeds — read persisted filters from localStorage (matching the seeds page)
  useSeedsPrefetch();

  // Threats — prefetch KEV vulnerabilities (default view: KEV=true)
  useKEVVulnerabilities({ kevFilter: true });

  // Metrics — prefetch key dashboard widget data
  useMetricsPrefetch();
}

/**
 * Prefetch seed data using persisted filters from localStorage.
 * Matches what the seeds page will request on mount.
 */
function useSeedsPrefetch() {
  const seedFilter = useMemo((): SeedFilter => {
    const defaultType: SeedType = 'domain';
    const stored = seedFilterStorage.get(defaultType);
    if (stored) {
      return { ...stored, type: defaultType };
    }
    const defaultSort = getDefaultSort(defaultType) as Sort;
    return {
      type: defaultType,
      sort: defaultSort,
    };
  }, []);

  useSeeds(seedFilter);
}

/**
 * Prefetch key metrics widget data.
 * Warms the React Query cache for the most common dashboard widgets.
 */
function useMetricsPrefetch() {
  const {
    dashboard: { period },
  } = useGlobalState();

  const daysAgo = getDaysAgo(Number(period));

  // VulnerabilityAnalysis — open vulnerability counts by severity
  useMy(
    {
      resource: 'statistic',
      query: `#my#status:O#${daysAgo}`,
      to: '#statistic#my#status:O#now&',
    },
    STALE_WHILE_REVALIDATE_CACHE
  );

  // VulnerabilityOverview — risk events over time
  useMy(
    {
      resource: 'statistic',
      query: `#event##risk##${daysAgo}`,
      to: '#statistic#event##risk##now&',
    },
    STALE_WHILE_REVALIDATE_CACHE
  );

  // AssetCounts — monitoring count statistics
  useGetMonitoringCounts({ countType: 'active_monitoring_count' });
}
